/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useEffect, useState } from "react"
import { Drawer, Button } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { useDispatch, useSelector } from "react-redux"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import { SellingTime } from "types/models/menu"
import { AppState } from "store/store.type"
import {
  getSellingTimeByID,
  useConflictConfirmPayload,
} from "utils/selling_time_helper"
import { ServiceHourList } from "components/list/ServiceHourList"
import { SellingTimeDropdown } from "components/dropdowns"
import { useSellingTimes } from "hooks/useSellingTimes"
import { actionCreator } from "store/store"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import useConfirmedMutation from "queries/hooks/useConfirmedMutation"
import { useUpdateDepartmentMutation } from "queries"
import { EventCategoryEng, EventActionEng } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"
import { ResponseWithError } from "types/api"
import { getDalErrMessage, parseJSONBasedErrorMsg } from "utils/misc"
import { refreshMenuAndCloseDrawers } from "utils/rtk_migration_helper"
import {
  drawerBodyStyle,
  drawerContentStyle,
  drawerFooterStyle,
} from "./styles"

interface SubDepartmentDrawerProps extends FormComponentProps {
  visible: boolean
  onClose(e: any): void
}

export const SubDepartmentDrawerComponent: FC<SubDepartmentDrawerProps> = ({
  visible,
  onClose,
}: SubDepartmentDrawerProps) => {
  const dispatch = useDispatch()
  const { sellingTimes } = useSellingTimes()
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    drawer: { subDepartment, createdSellingTimeID },
    isMartDepartmentStructure,
    isSellingTimeStructure,
    enablePartialMenu,
  } = useSelector((state: AppState) => state?.menu)
  const { isEditAllowed } = useMenuEditorStatus()

  const {
    flags: { updateDepartment: isRtkUpdateDepartmentEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [selectedSellingTime, setSelectedSellingTime] = useState<
    SellingTime | undefined
  >(sellingTimes[0])

  const textMap = useTranslationsMapTyped({
    updateSuccess: "menu.notifications.updateCategory.successMessage",
    sellingTimeLabel: "menu.sellingTime.label.sellingTime",
    editCategory: "menu.drawers.category.editCategory",
    saveChanges: "menu.buttons.saveChanges",
  })

  const conflictConfirmPayload = useConflictConfirmPayload(subDepartment)

  const [updateDepartmentRtk] = useConfirmedMutation(
    useUpdateDepartmentMutation,
    conflictConfirmPayload.showConfirm
      ? conflictConfirmPayload.confirmOptions
      : undefined,
  )

  useEffect(() => {
    if (visible) {
      setSelectedSellingTime(
        getSellingTimeByID(subDepartment?.sellingTimeID, sellingTimes),
      )
    }
  }, [visible, subDepartment, sellingTimes])

  // set selected selling time as the new created selling time if created while this drawer still opened
  useEffect(() => {
    if (createdSellingTimeID) {
      setSelectedSellingTime(
        getSellingTimeByID(createdSellingTimeID, sellingTimes),
      )
      // clear the created selling time
      dispatch(
        actionCreator.menu.toggleDrawer({
          createdSellingTimeID: null,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingTimes])

  const departmentID = subDepartment?.itemClassID ?? ""
  const sellingTimeID = selectedSellingTime?.sellingTimeID

  const handleSave = async () => {
    if (!departmentID || !sellingTimeID) return

    const data = {
      departmentID,
      sellingTimeID,
    }

    // While migration flags are loading, do not fall back to old Redux: the enabled bit is
    // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
    // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
    // the trigger UI while loading so clicks are not silently dropped.
    if (!isRtkFlagLoading && isRtkUpdateDepartmentEnabled) {
      const result = await updateDepartmentRtk({
        merchantID,
        menuGroupID,
        merchantGroupID,
        departmentID,
        data,
        showError: true,
        getErrorNotificationContent: menuGroupID
          ? ({ data: errorData, defaultMessage, trace }) => {
              const responseMessage = (errorData as ResponseWithError)?.message
              const message =
                parseJSONBasedErrorMsg(responseMessage || "") ||
                getDalErrMessage(responseMessage || "") ||
                defaultMessage

              return {
                message,
                ...(trace ? { description: `trace=${trace}` } : {}),
              }
            }
          : undefined,
        showSuccess: true,
        successOptions: { message: textMap.updateSuccess },
      })

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.updateDepartment,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkUpdateDepartmentEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "updateDepartment",
        },
      })

      if (!result) return

      if ("data" in result) {
        refreshMenuAndCloseDrawers({
          dispatch,
          merchantID,
          merchantGroupID,
          menuGroupID,
          isMartDepartmentStructure,
          isSellingTimeStructure,
          enablePartialMenu,
        })
      }
    } else if (!isRtkFlagLoading) {
      dispatch(
        actionCreator.menu.updateDepartment({
          merchantID,
          menuGroupID,
          merchantGroupID,
          departmentID,
          data,
          successMessage: textMap.updateSuccess,
          ...conflictConfirmPayload,
        }),
      )

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.updateDepartment,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkUpdateDepartmentEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "updateDepartment",
        },
      })
    }
  }

  return (
    <Drawer
      destroyOnClose
      title={textMap.editCategory}
      width={450}
      open={visible}
      onClose={onClose}
    >
      <div css={drawerBodyStyle}>
        <div
          css={css`
            flex: 1 0 auto;
          `}
        >
          <div css={drawerContentStyle}>
            <Fragment>
              <Form.Item required label={textMap.sellingTimeLabel}>
                <SellingTimeDropdown
                  data-testid="selling-time-dropdown"
                  disabled={!isEditAllowed}
                  sellingTimes={sellingTimes}
                  selectedSellingTime={selectedSellingTime}
                  onChange={setSelectedSellingTime}
                />
              </Form.Item>
              <ServiceHourList sellingTime={selectedSellingTime} />
            </Fragment>
          </div>
        </div>
        <div css={drawerFooterStyle}>
          <Button
            data-testid="save-button"
            type="primary"
            block
            onClick={handleSave}
            disabled={
              subDepartment?.sellingTimeID ===
                selectedSellingTime?.sellingTimeID || isRtkFlagLoading
            }
          >
            {textMap.saveChanges}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
export const SubDepartmentDrawer = Form.create<SubDepartmentDrawerProps>({})(
  SubDepartmentDrawerComponent,
)
